import React, { useState, useEffect } from 'react';
import { sessionHeaders } from '../services/auth';

const NEON = '/.netlify/functions/neon';

function neonQuery(query, params = {}) {
  return fetch(NEON, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...sessionHeaders() },
    body: JSON.stringify({ query, params }),
  }).then(r => r.json());
}

export default function ConversionSettings() {
  const [config, setConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // event_key being saved
  const [error, setError] = useState(null);
  const [edits, setEdits] = useState({}); // { event_key: { field: value } }
  const [initialized, setInitialized] = useState(false);

  useEffect(() => { loadConfig(); }, []);

  async function loadConfig() {
    setLoading(true);
    setError(null);
    try {
      let res = await neonQuery('conversion_config_get');
      if (!res.rows || res.rows.length === 0) {
        // Table doesn't exist or is empty — initialize it
        res = await neonQuery('conversion_config_init');
        setInitialized(true);
      }
      setConfig(res.rows || []);
    } catch (e) {
      // If the table doesn't exist, init it
      try {
        const res = await neonQuery('conversion_config_init');
        setConfig(res.rows || []);
        setInitialized(true);
      } catch (e2) {
        setError(e2.message);
      }
    } finally {
      setLoading(false);
    }
  }

  function handleEdit(eventKey, field, value) {
    setEdits(prev => ({
      ...prev,
      [eventKey]: { ...(prev[eventKey] || {}), [field]: value },
    }));
  }

  function hasEdits(eventKey) {
    return edits[eventKey] && Object.keys(edits[eventKey]).length > 0;
  }

  async function saveRow(eventKey) {
    const changes = edits[eventKey];
    if (!changes) return;
    setSaving(eventKey);
    try {
      const params = { event_key: eventKey, ...changes };
      // Convert enabled string to boolean if needed
      if (params.enabled !== undefined) params.enabled = params.enabled === true || params.enabled === 'true';
      const res = await neonQuery('conversion_config_update', params);
      setConfig(res.rows || []);
      setEdits(prev => {
        const next = { ...prev };
        delete next[eventKey];
        return next;
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  }

  function getDisplayValue(row, field) {
    const edit = edits[row.event_key];
    if (edit && edit[field] !== undefined) return edit[field];
    return row[field];
  }

  const thStyle = { textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)', fontSize: 11 };
  const tdStyle = { padding: '6px 12px', verticalAlign: 'middle' };
  const inputStyle = {
    fontSize: 12, padding: '5px 8px', border: '1px solid var(--gray-200)', borderRadius: 5,
    fontFamily: 'Montserrat', width: '100%', boxSizing: 'border-box',
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Conversion Settings</h2>
          <p style={{ fontSize: 11.5, color: 'var(--gray-500)', margin: '4px 0 0' }}>
            Configure Meta CAPI event names and attribution values for each conversion type.
          </p>
        </div>
      </div>

      {initialized && (
        <div style={{ padding: '8px 14px', background: '#e6f9f0', borderRadius: 6, fontSize: 12, color: '#1a8a4a', marginBottom: 12, fontWeight: 600 }}>
          Config table created and seeded with defaults.
        </div>
      )}

      {error && (
        <div style={{ padding: '8px 14px', background: '#fde8e8', borderRadius: 6, fontSize: 12, color: '#c0392b', marginBottom: 12 }}>
          Error: {error}
        </div>
      )}

      <div className="card">
        <div className="card-header">Event Configuration</div>
        {loading ? (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={thStyle}>Event Type</th>
                  <th style={thStyle}>Label</th>
                  <th style={thStyle}>Meta Event Name</th>
                  <th style={{ ...thStyle, textAlign: 'right', width: 100 }}>Value ($)</th>
                  <th style={{ ...thStyle, textAlign: 'center', width: 80 }}>Enabled</th>
                  <th style={{ ...thStyle, textAlign: 'center', width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {config.map((row, i) => {
                  const isPurchase = row.event_key === 'purchase' || row.event_key === 'google_purchase';
                  const isGoogle = row.event_key.startsWith('google_');
                  return (
                    <tr key={row.event_key} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                      <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--navy)' }}>
                        <div>{row.event_key}</div>
                        {isGoogle && <div style={{ fontSize: 9, color: '#1a73e8', fontWeight: 700 }}>GOOGLE ADS</div>}
                      </td>
                      <td style={tdStyle}>
                        <input
                          style={inputStyle}
                          value={getDisplayValue(row, 'label')}
                          onChange={e => handleEdit(row.event_key, 'label', e.target.value)}
                        />
                      </td>
                      <td style={tdStyle}>
                        <input
                          style={inputStyle}
                          value={getDisplayValue(row, 'meta_event_name')}
                          onChange={e => handleEdit(row.event_key, 'meta_event_name', e.target.value)}
                          placeholder={isGoogle ? 'customers/ID/conversionActions/ID' : ''}
                        />
                        {isGoogle && (
                          <div style={{ fontSize: 9, color: 'var(--gray-400)', marginTop: 2 }}>
                            Conversion Action resource name
                          </div>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {isPurchase ? (
                          <div style={{ fontSize: 11, color: 'var(--gray-500)', textAlign: 'right', padding: '0 4px' }}>
                            Order total
                          </div>
                        ) : (
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            style={{ ...inputStyle, textAlign: 'right' }}
                            value={getDisplayValue(row, 'value')}
                            onChange={e => handleEdit(row.event_key, 'value', e.target.value)}
                          />
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={getDisplayValue(row, 'enabled') === true || getDisplayValue(row, 'enabled') === 'true'}
                          onChange={e => handleEdit(row.event_key, 'enabled', e.target.checked)}
                          style={{ width: 16, height: 16, cursor: 'pointer' }}
                        />
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        {hasEdits(row.event_key) && (
                          <button
                            onClick={() => saveRow(row.event_key)}
                            disabled={saving === row.event_key}
                            style={{
                              fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 5,
                              border: 'none', background: 'var(--orange)', color: '#fff', cursor: 'pointer',
                              opacity: saving === row.event_key ? 0.6 : 1,
                              fontFamily: 'Montserrat',
                            }}
                          >
                            {saving === row.event_key ? 'Saving...' : 'Save'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ marginTop: 16, fontSize: 11, color: 'var(--gray-500)', lineHeight: 1.6 }}>
        <strong>How it works:</strong> When a webhook fires (purchase, newsletter signup, SMS signup, etc.),
        the system reads this config to determine which Meta CAPI event to send and what dollar value to attribute.
        The "purchase" event always uses the actual order total. Disabling an event stops it from firing to Meta/Google
        but still logs it in the conversions table.
        <br /><br />
        <strong>Google Ads:</strong> The "google_purchase" row controls Google Enhanced Conversions.
        Set the "Meta Event Name" field to your Google Ads conversion action resource name
        (e.g. <code>customers/1234567890/conversionActions/987654321</code>).
        Find yours on the Google Campaigns page under conversion actions.
      </div>
    </div>
  );
}
